<template>
  <div class="community-publicity-manage-container">
    <el-row :gutter="16">
      <!-- 左侧公示类型导航：保留原有类型切换语义与事件，不改任何业务逻辑 -->
      <el-col :xs="24" :sm="24" :md="7" :lg="5">
        <div class="ds-nav-card">
          <div class="ds-nav-card__header">{{ $t('communityPublicityManage.pubTypeList') }}</div>
          <ul class="ds-nav-card__list">
            <li v-for="(item, index) in pubTypes" :key="index" class="ds-nav-card__item"
              :class="{ 'is-active': conditions.pubType === item.statusCd }" @click="handlePubTypeChange(item)">
              <i class="el-icon-document"></i>
              <span class="ds-nav-card__name">{{ item.name }}</span>
            </li>
          </ul>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="17" :lg="19">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('communityPublicityManage.page.title')"
          :subtitle="$t('communityPublicityManage.page.subtitle')">
          <template #extra>
            <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
              {{ $t('common.add') }}
            </el-button>
          </template>
        </ds-page-header>

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.title" clearable class="w-full"
                :placeholder="$t('communityPublicityManage.titlePlaceholder')" @keyup.enter.native="handleSearch" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="refreshList">
            <template #left>
              <span class="ds-panel__title">{{ $t('communityPublicityManage.tableTitle') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('communityPublicityManage.page.empty')" @retry="refreshList">
            <el-table-column prop="headerImg" :label="$t('communityPublicityManage.headerImg')" width="110"
              align="center">
              <template slot-scope="scope">
                <el-image style="width: 60px; height: 60px; cursor: pointer;"
                  :src="scope.row.headerImg || '/img/noPhoto.jpg'"
                  :preview-src-list="[scope.row.headerImg || '/img/noPhoto.jpg']" fit="cover" class="ds-img-preview" />
              </template>
            </el-table-column>
            <el-table-column prop="title" :label="$t('communityPublicityManage.title')" align="left" min-width="180"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.title || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="pubTypeName" :label="$t('communityPublicityManage.pubType')" align="center" width="120">
              <template slot-scope="scope">
                <ds-status-tag type="info" :label="scope.row.pubTypeName || '-'" />
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('communityPublicityManage.createTime')" align="center"
              width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="createUserName" :label="$t('communityPublicityManage.publisher')" align="center"
              width="120">
              <template slot-scope="scope">
                {{ scope.row.createUserName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="140" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="handleEdit(scope.row)">
                  {{ $t('common.edit') }}
                </el-button>
                <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
                  {{ $t('common.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #summary>
              <span class="ds-tip-text">{{ $t('communityPublicityManage.page.tip') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </el-col>
    </el-row>

    <view-image ref="viewImage" />
    <delete-community-publicity ref="deleteDialog" @success="fetchData" />
  </div>
</template>

<script>
import { getCommunityPublicityList } from '@/api/community/communityPublicityManageApi'
import { getDict } from '@/api/community/communityApi'
import ViewImage from '@/components/system/viewImage'
import DeleteCommunityPublicity from '@/components/community/deleteCommunityPublicity'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CommunityPublicityManageList',
  components: {
    ViewImage,
    DeleteCommunityPublicity,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      pubTypes: [],
      tableData: [],
      conditions: {
        pubType: '',
        title: '',
        communityId: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.conditions.communityId = getCommunityId()
    this.initData()
  },
  methods: {
    async initData() {
      try {
        this.loading = true
        await this.getPubTypes()
        if (this.pubTypes.length > 0) {
          this.conditions.pubType = this.pubTypes[0].statusCd
        }
        await this.fetchData()
      } catch (error) {
        console.error('初始化数据失败:', error)
      } finally {
        this.loading = false
      }
    },
    async getPubTypes() {
      try {
        const data = await getDict('community_publicity', 'pub_type')
        this.pubTypes = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取公示类型失败:', error)
      }
    },
    async fetchData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.conditions
        }
        const { data, total } = await getCommunityPublicityList(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取公示列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('communityPublicityManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    /**
     * 刷新当前页（工具条刷新 / 错误态重试）
     */
    refreshList() {
      this.fetchData()
    },
    handlePubTypeChange(item) {
      this.conditions.pubType = item.statusCd
      this.pagination.current = 1
      this.fetchData()
    },
    handleSearch() {
      this.pagination.current = 1
      this.fetchData()
    },
    handleReset() {
      this.conditions.title = ''
      this.pagination.current = 1
      this.fetchData()
    },
    handleAdd() {
      this.$router.push('/views/community/addCommunityPublicity')
    },
    handleEdit(row) {
      this.$router.push(`/views/community/editCommunityPublicity?pubId=${row.pubId}`)
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.fetchData()
    },
    /**
     * 公示时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.community-publicity-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧公示类型导航卡片 */
.ds-nav-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: var(--ds-spacing-sm);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-xs);
    border-radius: var(--ds-radius-sm);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    cursor: pointer;
    transition: background-color var(--ds-transition-fast),
      color var(--ds-transition-fast);

    &:hover {
      background-color: rgba(var(--ds-primary-rgb), 0.08);
      color: var(--ds-primary);
    }

    &.is-active {
      background-color: var(--ds-primary);
      color: #fff;
      font-weight: 600;
    }
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 图片预览圆角 */
.ds-img-preview {
  border-radius: var(--ds-radius-sm);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
